import React from 'react';
import { ShowSection, SectionRow, FieldListLayout, ShowPageLayout } from 'framework/ui/layout';
import ViewProjectPageMenu from 'domain/projects/project/Show/ViewProjectPageMenu';
import ProjectEntityHeader from 'domain/projects/project/shared/ProjectEntityHeader';
import SingleProjectBreadcrumbs from 'domain/projects/project/shared/SingleProjectBreadcrumbs';
import EditProjectButton from 'domain/projects/project/shared/button/EditProjectButton';
import DeleteProjectButton from 'domain/projects/project/shared/button/DeleteProjectButton';
import { BooleanField, CommaListField, TextField, useFieldHelper } from 'framework/ui/field';
import { UPDATE_PROJECT } from 'domain/shared/project';
import { BudgetLinkField, BudgetChildLinkField } from 'domain/shared/budget';
import { ArtifactStorageFileInformationSection } from 'domain/shared/artifactStorage';
import { AccountLinkField } from 'domain/shared/account';
import ArchiveObjects from '../shared/button/ArchiveObjects';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import ProjectField from 'infrastructure/integrations/redmine/ProjectField';

const ViewProjectPageContent = ({
  isSensitiveDataIsShown = false,
  toggleSensitiveDataButton = <></>,
}: {
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: React.ReactElement;
}) => {
  const { getValue: getTitle } = useFieldHelper({ source: isSensitiveDataIsShown ? 'realProjectName' : 'name' });

  return (
    <ShowPageLayout
      headerButtons={[
        toggleSensitiveDataButton,
        <EditProjectButton key="edit-project" permissionToCheck={UPDATE_PROJECT} />,
        <ArchiveObjects key="archive-objects" />,
        <DeleteProjectButton key="delete-project" />,
      ]}
      menu={<ViewProjectPageMenu />}
      breadcrumbs={<SingleProjectBreadcrumbs />}
      entityHeader={<ProjectEntityHeader entityTitle={getTitle() || '-'} />}
      hasSeparateSections
    >
      <ShowSection title="Project information">
        <SectionRow>
          <TextField source="name" isVisible={!isSensitiveDataIsShown} />
          <TextField label="Name" source="realProjectName" isVisible={isSensitiveDataIsShown} />
          <ProjectField source="taskTrackerAlias" nameSource="taskTrackerAlias" label="Redmine Project Alias" />
          <AccountLinkField label="Account" source="accountName" idSource="accountId" idValue="" isInternal={false} />
        </SectionRow>
        <SectionRow>
          <BudgetLinkField label="Default Budget" source="defaultBudgetId" nameSource="defaultBudget.name" />
          <FieldListLayout source="availableBudgets" label="Available Budgets">
            <BudgetChildLinkField />
          </FieldListLayout>
          <BooleanField source="assignmentForbidden" label="Assignment Forbidden" />
        </SectionRow>
        <SectionRow>
          <EmployeeLinkFieldWithNameLoading source="staffingCoordinatorAlias" label="Staffing Coordinator" />

          <CommaListField
            label="Project Manager"
            source="projectManagers"
            isNeedJoin={false}
            mapValue={value => {
              return <EmployeeLinkFieldWithNameLoading label="" externalId={value} />;
            }}
          />

          <CommaListField
            label="Backup Project Manager"
            source="backupProjectManagers"
            isNeedJoin={false}
            mapValue={value => {
              return <EmployeeLinkFieldWithNameLoading label="" externalId={value} />;
            }}
          />
        </SectionRow>
      </ShowSection>
      <ArtifactStorageFileInformationSection
        title="SOW Information"
        source="sow"
        prevSource="sow"
        isVisible={data => data.sow != null}
      />
    </ShowPageLayout>
  );
};

export default ViewProjectPageContent;
